import { NotOnMobile, OnMobile } from "lib/@components/mobile"
import { DesktopDatePicker, MobileDatePicker } from "@mui/x-date-pickers"
import { TextField } from "@mui/material"
import { mergeDeep } from "@apollo/client/utilities"
import { bind } from "lib/@components/binding/bind"

export const BoundDatePicker = bind(<DatePicker />, { extract: (v) => v })

/**
 * Responsive date picker (mobile/desktop variants) with a read-only text input.
 *
 * @param {Object} params
 * @param {*} params.value - The current date value.
 * @param {Function} params.onChange - Called with the new date value.
 * @param {Object} [params.textFieldProps] - Props merged into the rendered TextField.
 * @param {Object} [params.popperProps] - Props for the desktop picker's Popper.
 * @param {boolean} [params.suppressEmptyError=false] - When true, hides the MUI error indicator
 *     ("Invalid date" red state) that MUI applies to an EMPTY value. This is presentation only —
 *     it does not validate anything, and the caller remains responsible for validating emptiness
 *     (e.g. required-field checks). An explicit `textFieldProps.error === true` still wins.
 */
export function DatePicker({ value, onChange, textFieldProps, popperProps, suppressEmptyError = false, ...props }) {
    return (
        <>
            <OnMobile>
                <MobileDatePicker
                    inputFormat="DD/MM/YYYY"
                    onChange={onChange}
                    value={value}
                    renderInput={renderInput}
                    {...props}
                />
            </OnMobile>
            <NotOnMobile>
                <DesktopDatePicker
                    inputFormat="DD/MM/YYYY"
                    onChange={onChange}
                    value={value}
                    PopperProps={{ ...popperProps }}
                    renderInput={renderInput}
                    {...props}
                />
            </NotOnMobile>
        </>
    )

    // MUI flags an empty picker as error:"Invalid date". Opt-in via suppressEmptyError to hide
    // that on empty values (still respects an explicit textFieldProps.error=true override).
    function renderInput(p) {
        if (suppressEmptyError && !value && textFieldProps?.error !== true) p = { ...p, error: false }
        return <TextField fullWidth {...mergeDeep(textFieldProps, p, { inputProps: { readOnly: true } })} />
    }
}
